import { useMemo, useCallback, useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import DataTable, { TypeColumn } from '@/components/DataTable';
import { CardContainer } from '@/layout/Card';
import { Flex } from '@/layout';
import Loading from '@/components/UI/Loading';
import { getSharedToMe, getSharedContents } from '@/api/document.api';

import { convertToFileSize, convertToData } from '@/utils/convert';
import { Documents, Download, Folder } from '@/components/Icon';
import { theme } from '@/theme/Theme';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { openStorageDownload } from '@/utils/storageDownload';
import { errorHandler } from '@/utils/toastHandler';

type DataRow = {
  id: string;
  name: string;
  type: 'folder' | 'file';
  size?: string;
  extension?: string;
  path?: string;
  category?: any;
  created_at: string;
  updated_at: string;
  share_id: number;
  owner: {
    id: number;
    name: string;
  };
  action: string;
  added: boolean;
};



const DocumentList = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const tabLabel = t('pages.dealDetail.manageFiles.myFolder.table.columns', { returnObjects: true });

  const [docLibrary, setDocLibrary] = useState<DataRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeShare, setActiveShare] = useState<{ id: number; owner: DataRow['owner'] } | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: number; name: string }>>([]);

  const openFolder = async (folder: DataRow) => {
    const share = activeShare || { id: folder.share_id, owner: folder.owner };
    setIsLoading(true);
    try {
      const response = await getSharedContents(share.id, Number(folder.id));
      const items: FolderOrFileItem[] = response.data.data;
      setDocLibrary(items.map((item) => ({
        ...item,
        id: String(item.id),
        size: item.size ? convertToFileSize(Number(item.size)) : '-',
        updated_at: convertToData(item.updated_at),
        share_id: share.id,
        owner: share.owner,
        action: item.path || '',
        added: false,
      })));
      setActiveShare(share);
      setBreadcrumbs((previous) => {
        const index = previous.findIndex((item) => item.id === Number(folder.id));
        return index === -1
          ? [...previous, { id: Number(folder.id), name: folder.name }]
          : previous.slice(0, index + 1);
      });
    } catch {
      errorHandler(t('error.server'));
    } finally {
      setIsLoading(false);
    }
  };
  const fetchSharedDocuments = useCallback(async (page: number = 1, perPage: number = 10) => {
    setIsLoading(true);
    try {
      const params = {
        per_page: perPage,
        page: page
      };

      const response = (await getSharedToMe(params)).data as SharedToMeListResponse;

      if (response.success) {
        const resData = response.data.data;
        const meta = response.data.meta;

        const doc: DataRow[] = resData.map(
          (data): DataRow => ({
            id: data.id.toString(),
            name: data.name,
            type: data.type,
            size: data.size ? convertToFileSize(Number(data.size)) : '-',
            extension: data.extension,
            path: data.path,
            category: data.category,
            created_at: data.created_at,
            updated_at: convertToData(data.updated_at),
            share_id: data.share_id,
            owner: data.owner,
            action: data.path || '',
            added: false,
          })
        );

        setDocLibrary(doc);
        setPagination({
          current_page: meta.current_page,
          per_page: meta.per_page,
          total: meta.total,
          last_page: meta.last_page
        });
        setCurrentPage(meta.current_page);
        setPageSize(meta.per_page);
      }
    } catch (error) {
      console.error('Error fetching shared documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedDocuments();
  }, [fetchSharedDocuments]);

  const handlePageChange = useCallback((page: React.SetStateAction<number>) => {
    const pageNum = typeof page === 'function' ? page(currentPage) : page;
    setCurrentPage(pageNum);
    fetchSharedDocuments(pageNum, pageSize);
  }, [fetchSharedDocuments, pageSize, currentPage]);

  const handlePageSizeChange = useCallback((perPage: React.SetStateAction<number>) => {
    const perPageNum = typeof perPage === 'function' ? perPage(pageSize) : perPage;
    setPageSize(perPageNum);
    setCurrentPage(1);
    fetchSharedDocuments(1, perPageNum);
  }, [fetchSharedDocuments, pageSize]);

  const columns: ColumnDef<DataRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: tabLabel[0],
        cell: ({ row }) => (
          <>
            {
              row.original.type === 'folder' ? (
                <Flex gridGap={'10px'}>
                  <Folder.FolderSVG />
                  <Button type="button" variant="primary-invert" size="sm" onClick={() => openFolder(row.original)}>
                    {row.original.name}
                  </Button>
                </Flex>
              ) : (
                <Flex gridGap={'10px'}>
                  <Documents.DocumentsSVG fill={theme.colors.primary500} />
                  <Text>{row.original.name}</Text>
                </Flex>
              )
            }
          </>
        ),
      },
      {
        accessorKey: 'type',
        header: tabLabel[1],
        cell: ({ row }) => (<>
          {
            row.original.type === 'folder' ? '-' : <TypeColumn text={row.original.extension || row.original.type} />
          }</>),
      },
      {
        accessorKey: 'size',
        header: tabLabel[2],
        cell: ({ row }) => (<>
          {
            row.original.type === 'folder' ? '-' : row.original.size
          }</>),
      },
      {
        accessorKey: 'owner',
        header: t('pages.docLibrary.sharedBy'),
        cell: ({ row }) => <Text>{row.original.owner.name}</Text>,
      },
      {
        accessorKey: 'updated_at',
        header: tabLabel[3],
      },
      {
        accessorKey: 'action',
        header: tabLabel[4],
        cell: ({ row }) => {
          const { type, path } = row.original;
          return (
            <Flex>
              {
                type !== 'folder' && path && (
                  <Button
                    size="sm"
                    type="button"
                    onClick={async () => {
                      try {
                        await openStorageDownload(path);
                      } catch {
                        errorHandler(t('pages.docLibrary.downloadFailed'));
                      }
                    }}
                  >
                    <Download.DownloadSVG />
                  </Button>
                )
              }
            </Flex>
          );
        },
      },],
    [tabLabel]
  ); return (
    <CardContainer padding={'30px 0'}>
      {activeShare && (
        <Flex gridGap="10px" flexWrap="wrap" marginBottom="15px">
          <Button type="button" size="sm" disabled={isLoading} onClick={() => {
            setActiveShare(null);
            setBreadcrumbs([]);
            fetchSharedDocuments(currentPage, pageSize);
          }}>{t('buttons.back')}</Button>
          {breadcrumbs.map((item, index) => (
            <Button key={item.id} type="button" size="sm" variant="primary-invert"
              disabled={isLoading || index === breadcrumbs.length - 1}
              onClick={() => openFolder({ ...item, id: String(item.id), share_id: activeShare.id, owner: activeShare.owner } as DataRow)}>
              {item.name}
            </Button>
          ))}
        </Flex>
      )}
      {!isLoading ? (<DataTable
        key={activeShare ? `folder-${breadcrumbs[breadcrumbs.length - 1]?.id}` : 'shared-root'}
        columns={columns}
        data={docLibrary}
        canSearch
        {...(!activeShare ? {
          page: currentPage,
          perPage: pageSize,
          total: pagination.total,
          setPage: handlePageChange,
          setPerPage: handlePageSizeChange,
        } : {})}
      />
      ) : (
        <Loading />
      )}
    </CardContainer>
  );
};

export default DocumentList;
